import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, type UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {type AxiosError} from 'axios'

import {riskSurveyMutations} from '../../api'
import type {RiaDetailDto, UpdateRiaDetailsRequest} from '../../types'
import {useRiskSurveyTranslation} from '../useRiskSurveyTranslation'

import {riskSurveyQueryKeys} from './riskSurveyQueryKeys'

type Variables = {
  riaId: string
  data: UpdateRiaDetailsRequest
}

export const useUpdateRiaDetails = (
  options?: UseMutationOptions<RiaDetailDto, AxiosError, Variables>
) => {
  const {t} = useRiskSurveyTranslation()
  const queryClient = useQueryClient()

  return useMutation<RiaDetailDto, AxiosError, Variables>({
    mutationFn: ({riaId, data}) => riskSurveyMutations.updateRiaDetails(riaId, data),
    ...options,
    onSuccess: (data, variables, onMutateResult, context) => {
      spawnSuccessToast(t('detail.detailsUpdateSuccess'))
      void options?.onSuccess?.(data, variables, onMutateResult, context)
    },
    onError: (error, variables, onMutateResult, context) => {
      spawnErrorToast(t('detail.detailsUpdateError'))
      void options?.onError?.(error, variables, onMutateResult, context)
    },
    onSettled: (data, error, variables, onMutateResult, context) => {
      void queryClient.invalidateQueries({
        queryKey: riskSurveyQueryKeys.rias.detail(variables.riaId)
      })
      void queryClient.invalidateQueries({
        queryKey: riskSurveyQueryKeys.rias.detailsAuditLog(variables.riaId)
      })
      void options?.onSettled?.(data, error, variables, onMutateResult, context)
    }
  })
}
